import { useEffect, useMemo, useRef, useState } from 'react';
import FeexPayModal, { type FeexPayWidgetState } from '@/components/feature/FeexPayModal';
import {
  FEEXPAY_MIN_AMOUNT,
  computeFeexPayAmount,
  getFeexPayActiveCountries,
  getFeexPayGatewayCurrency,
  isFeexPayAmountValid,
  resolveFeexPayNetworks,
  resolveXofRateFor,
  type FeexPayConfig,
} from '@/lib/feexpay';
import { prepareShopPayment, readWidgetOutcome, verifyShopPayment } from '@/lib/shopPayment';
import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ShopPaymentModal — la fenêtre de paiement d'une commande de boutique.
//
// Même principe que le tunnel d'abonnement (/dashboard/abonnement/choisir) :
//  • le montant de la commande est déjà pré-rempli (modifiable) ;
//  • le client choisit son pays / opérateur : le montant est converti dans la
//    devise réellement encaissée (XOF / XAF) ;
//  • la modale FeexPay s'ouvre ENSUITE ici même : c'est elle qui demande les
//    détails du paiement. Aucune information n'est envoyée avant ce clic.
//  • la commande n'est marquée payée qu'après confirmation de l'opérateur.
// ─────────────────────────────────────────────────────────────

interface ShopPaymentModalProps {
  orderId: number;
  /** Total de la commande, dans la devise de la boutique. */
  total: number;
  /** Devise de la boutique (panier). */
  currency: string;
  buyerName: string;
  buyerEmail: string;
  buyerPhone: string;
  merchantName: string;
  onClose: () => void;
  onPaid: (info: { amount: number; currency: string; reference: string }) => void;
}

type Phase = 'form' | 'preparing' | 'widget' | 'verifying' | 'pending' | 'paid';

const formatMoney = (value: number): string =>
  (Number(value) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 });

export default function ShopPaymentModal({
  orderId,
  total,
  currency,
  buyerName,
  buyerEmail,
  buyerPhone,
  merchantName,
  onClose,
  onPaid,
}: ShopPaymentModalProps) {
  const [config, setConfig] = useState<FeexPayConfig | null>(null);
  const [amountInput, setAmountInput] = useState(String(total));
  const [country, setCountry] = useState('');
  const [network, setNetwork] = useState('');
  const [phone, setPhone] = useState(buyerPhone || '');
  const [phase, setPhase] = useState<Phase>('form');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [widget, setWidget] = useState<FeexPayWidgetState | null>(null);
  const [paid, setPaid] = useState<{ amount: number; currency: string; reference: string } | null>(null);
  const lastTxnRef = useRef('');
  const [widgetOk, setWidgetOk] = useState(false);
  const reference = widget?.reference || '';

  // Configuration FeexPay (pays / opérateurs / taux) — comme sur la page
  // d'abonnement : le SuperAdmin définit les opérateurs disponibles.
  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const { data } = await supabase
          .from('payment_providers')
          .select('config, enabled')
          .eq('provider', 'feexpay')
          .maybeSingle();
        if (!active) return;
        const cfg = ((data?.config || {}) as FeexPayConfig) || null;
        setConfig(cfg);
        const countries = getFeexPayActiveCountries(cfg);
        const defaultCountry = String(cfg?.country || '') || countries[0]?.code || '';
        setCountry(defaultCountry);
        const nets = resolveFeexPayNetworks(cfg).filter((o) => o.enabled && o.country === defaultCountry);
        const defaultNetwork = String(cfg?.network || '');
        setNetwork(nets.some((o) => o.slug === defaultNetwork) ? defaultNetwork : (nets[0]?.slug || ''));
      } catch {
        /* on garde les valeurs saisies */
      }
    })();
    return () => { active = false; };
  }, []);

  const countries = useMemo(() => getFeexPayActiveCountries(config), [config]);
  const operators = useMemo(
    () => resolveFeexPayNetworks(config).filter((o) => o.enabled && o.country === country),
    [config, country],
  );

  const gatewayCurrency = getFeexPayGatewayCurrency(country);
  const rate = resolveXofRateFor(currency, config);
  const amount = Number(String(amountInput).replace(',', '.')) || 0;
  const gatewayAmount = computeFeexPayAmount(amount, rate, currency, undefined, gatewayCurrency);
  const belowTotal = amount + 0.01 < total;

  const handlePay = async () => {
    setError(null);
    setNotice(null);

    if (!amount || amount <= 0) {
      setError('Indiquez le montant à régler.');
      return;
    }
    if (belowTotal) {
      setError(`Le montant ne peut pas être inférieur au total de la commande (${formatMoney(total)} ${currency}).`);
      return;
    }
    if (!country || !network) {
      setError('Choisissez votre pays puis votre opérateur Mobile Money.');
      return;
    }
    if (phone.replace(/\D/g, '').length < 8) {
      setError('Indiquez le numéro Mobile Money qui sera débité.');
      return;
    }
    if (!isFeexPayAmountValid(gatewayAmount)) {
      setError(gatewayAmount < FEEXPAY_MIN_AMOUNT
        ? `Le montant converti (${formatMoney(gatewayAmount)} ${gatewayCurrency}) est inférieur au minimum de ${FEEXPAY_MIN_AMOUNT} ${gatewayCurrency}.`
        : `Le montant converti (${formatMoney(gatewayAmount)} ${gatewayCurrency}) dépasse le maximum autorisé par l’opérateur.`);
      return;
    }

    setPhase('preparing');
    try {
      const prepared = await prepareShopPayment({
        orderId,
        amount,
        country,
        network,
        gatewayCurrency,
        phone,
      });
      if (!prepared.shopId || !prepared.token) {
        throw new Error('La fenêtre de paiement n’est pas encore configurée. Contactez le commerçant.');
      }
      const operatorLabel = operators.find((o) => o.slug === network)?.label || '';
      setWidget({
        reference: prepared.reference,
        entityId: orderId,
        source: 'zifek_shop',
        amount: prepared.gatewayAmount,
        currency: prepared.gatewayCurrency,
        shopId: prepared.shopId,
        token: prepared.token,
        description: `Commande #${orderId}`,
        payCase: 'MOBILE',
        firstName: (buyerName || 'Client').split(' ')[0] || 'Client',
        email: buyerEmail || '',
        phone,
        country,
        network: operatorLabel.split(' ')[0] || '',
      });
      setPhase('widget');
    } catch (err) {
      setPhase('form');
      setError(err instanceof Error ? err.message : 'Le paiement n’a pas pu être préparé.');
    }
  };

  const verify = async (transactionReference = '', widgetSuccess = false, silent = false) => {
    if (!reference) {
      setPhase('form');
      setError('Référence de paiement perdue. Recommencez le paiement.');
      return;
    }
    if (!silent) setPhase('verifying');
    setError(null);
    try {
      const res = await verifyShopPayment({
        orderId,
        reference,
        widgetStatus: widgetSuccess ? 'success' : '',
        transactionReference,
      });
      if (res.status === 'paid') {
        const info = {
          amount: Number(res.amount) || amount,
          currency: String(res.currency || currency),
          reference,
        };
        setPaid(info);
        setPhase('paid');
        return;
      }
      if (res.status === 'failed') {
        setWidget(null);
        setPhase('form');
        setError(res.message || 'Le paiement a été refusé par l’opérateur. Vérifiez votre solde puis réessayez.');
        return;
      }
      setPhase('pending');
      setNotice(res.message || 'Le paiement n’est pas encore confirmé par l’opérateur.');
    } catch (err) {
      setPhase('pending');
      setNotice(err instanceof Error ? err.message : 'Vérification impossible pour le moment.');
    }
  };

  const handleWidgetResult = (result: unknown) => {
    const outcome = readWidgetOutcome(result);
    lastTxnRef.current = outcome.transactionReference;
    setWidgetOk(outcome.success);
    void verify(outcome.transactionReference, outcome.success);
  };

  // Tant que le paiement n'est pas confirmé, on réinterroge l'opérateur tout
  // seul : dès qu'il confirme, la modale passe en vert « Paiement effectué ».
  useEffect(() => {
    if (phase !== 'pending') return;
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      if (tries > 12) {
        window.clearInterval(timer);
        return;
      }
      void verify(lastTxnRef.current, widgetOk, true);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [phase, widgetOk, reference]);

  const busy = phase === 'preparing' || phase === 'verifying';

  const handleClose = () => {
    if (paid) {
      onPaid(paid);
      return;
    }
    if (!busy) onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6">
      <button
        type="button"
        aria-label="Fermer"
        onClick={() => { if (!busy) handleClose(); }}
        className="absolute inset-0 bg-foreground-950/50 cursor-pointer"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Paiement sécurisé"
        className="relative w-full max-w-lg bg-background-50 border border-background-200/70 rounded-xl shadow-xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-background-200/70">
          <div className="flex items-start gap-3 min-w-0">
            <span className="w-10 h-10 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center flex-shrink-0">
              <i className="ri-secure-payment-line text-lg"></i>
            </span>
            <div className="min-w-0">
              <h2 className="text-sm font-bold font-heading text-foreground-950">Paiement sécurisé</h2>
              <p className="text-[11px] text-foreground-500 truncate">
                Commande #{orderId} · {merchantName}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-500 hover:bg-background-100 disabled:opacity-40 transition-colors cursor-pointer flex-shrink-0"
            aria-label="Fermer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {phase === 'paid' && paid ? (
          <div className="px-5 py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-checkbox-circle-fill text-3xl text-accent-600"></i>
            </div>
            <h3 className="text-lg font-bold font-heading text-accent-700">Paiement effectué</h3>
            <p className="text-sm text-foreground-700 mt-1.5 leading-relaxed">
              Votre paiement de{' '}
              <strong>{formatMoney(paid.amount)} {paid.currency}</strong>{' '}
              a bien été encaissé. La commande <strong>#{orderId}</strong> est confirmée et payée.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-800 text-xs font-bold">
              <i className="ri-shield-check-line"></i>
              Statut : Payé
            </div>
            <p className="mt-3 text-[11px] text-foreground-500 break-all">Référence : {paid.reference}</p>
            <p className="mt-3 text-[11px] text-foreground-500 leading-relaxed">
              Le marchand a été prévenu et son portefeuille est crédité. Vos produits digitaux sont débloqués
              immédiatement dans votre espace client.
            </p>
            <button
              type="button"
              onClick={() => onPaid(paid)}
              className="mt-5 w-full py-2.5 rounded-full bg-accent-600 text-background-50 text-sm font-bold hover:bg-accent-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-check-line mr-1.5"></i>
              Terminer
            </button>
          </div>
        ) : (
        <div className="px-5 py-4 space-y-4">
          {/* Montant : déjà rempli avec le total, modifiable */}
          <div>
            <label htmlFor="shop-pay-amount" className="block text-xs font-medium text-foreground-700 mb-1.5">
              Montant à payer
            </label>
            <div className="flex items-center gap-2">
              <input
                id="shop-pay-amount"
                type="number"
                min={0}
                step="0.01"
                value={amountInput}
                onChange={(e) => setAmountInput(e.target.value)}
                disabled={phase !== 'form'}
                className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 disabled:opacity-60"
              />
              <span className="px-3 py-2.5 rounded-lg bg-background-100 text-xs font-semibold text-foreground-700 whitespace-nowrap">
                {currency}
              </span>
            </div>
            <p className="text-[11px] text-foreground-500 mt-1">
              Total de la commande : {formatMoney(total)} {currency}. Vous pouvez ajuster le montant, jamais en dessous.
            </p>
          </div>

          {phase === 'form' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="min-w-0">
                  <label htmlFor="shop-pay-country" className="block text-xs font-medium text-foreground-700 mb-1.5">Pays</label>
                  <select
                    id="shop-pay-country"
                    value={country}
                    onChange={(e) => {
                      const nextCountry = e.target.value;
                      setCountry(nextCountry);
                      const first = resolveFeexPayNetworks(config).find((o) => o.enabled && o.country === nextCountry);
                      setNetwork(first?.slug || '');
                    }}
                    className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                  >
                    {countries.length === 0 && <option value="">— Pays à configurer —</option>}
                    {countries.map((c) => (
                      <option key={c.code} value={c.code}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <label htmlFor="shop-pay-network" className="block text-xs font-medium text-foreground-700 mb-1.5">Opérateur</label>
                  <select
                    id="shop-pay-network"
                    value={network}
                    onChange={(e) => setNetwork(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                  >
                    {operators.length === 0 && <option value="">— Aucun opérateur —</option>}
                    {operators.map((o) => (
                      <option key={`${o.country}-${o.slug}`} value={o.slug}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="shop-pay-phone" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  Numéro Mobile Money
                </label>
                <input
                  id="shop-pay-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Ex. 01 23 45 67 89"
                  className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                />
              </div>

              <div className="rounded-lg bg-secondary-50 border border-secondary-100 px-3 py-2.5">
                <div className="flex items-center justify-between gap-3 text-xs">
                  <span className="text-foreground-600">Débité sur votre compte Mobile Money</span>
                  <span className="font-bold text-foreground-900 whitespace-nowrap">
                    {formatMoney(gatewayAmount)} {gatewayCurrency}
                  </span>
                </div>
                <p className="text-[11px] text-foreground-500 mt-1">
                  Conversion automatique : 1 {currency} ≈ {rate.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {gatewayCurrency}.
                </p>
              </div>
            </>
          )}

          {(phase === 'widget' || phase === 'verifying') && widget && (
            <div className="rounded-lg border border-background-200/70 bg-background-100/40 px-3 py-3">
              {phase === 'widget' ? (
                <FeexPayModal
                  state={widget}
                  onResult={handleWidgetResult}
                  onFallback={() => { void verify(); }}
                />
              ) : (
                <div className="flex items-center gap-2 text-xs text-foreground-600">
                  <i className="ri-loader-4-line animate-spin text-primary-500"></i>
                  Vérification du paiement auprès de l’opérateur…
                </div>
              )}
            </div>
          )}

          {phase === 'pending' && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-foreground-700 leading-relaxed">
              <p className="font-semibold text-foreground-900 mb-1">Paiement en attente de confirmation</p>
              <p>{notice || 'Validez la demande reçue sur votre téléphone, puis vérifiez à nouveau.'}</p>
              <button
                type="button"
                onClick={() => { void verify(); }}
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-foreground-950 text-background-50 text-[11px] font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-800 transition-colors"
              >
                <i className="ri-refresh-line"></i>
                Vérifier à nouveau
              </button>
            </div>
          )}

          {error && (
            <div className="px-3 py-2.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs flex items-start gap-2">
              <i className="ri-error-warning-line mt-0.5"></i>
              <span className="min-w-0">{error}</span>
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-center gap-2 pt-1">
            {phase === 'form' || phase === 'pending' ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    if (phase === 'pending') {
                      void verify(lastTxnRef.current, widgetOk);
                      return;
                    }
                    void handlePay();
                  }}
                  className="w-full sm:flex-1 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className={phase === 'pending' ? 'ri-refresh-line' : 'ri-lock-line'}></i>
                  {phase === 'pending'
                    ? 'Vérifier le paiement'
                    : `Payer ${formatMoney(amount || total)} ${currency}`}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full sm:w-auto px-4 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Plus tard
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="w-full py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200/70 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-time-line mr-1.5"></i>
                Paiement en cours — fermer
              </button>
            )}
          </div>

          <p className="text-[11px] text-foreground-400 leading-relaxed text-center">
            Paiement sécurisé par Zifek. Votre commande est confirmée dès que le paiement est validé.
          </p>
        </div>
        )}
      </div>
    </div>
  );
}